/**
 * The sound catalogue: what each cue sounds like, and which game event fires it.
 *
 * **Recorded where there is a recording, synthesised where there is not** (since 2026-09-27).
 * Every cue still carries its oscillator, sweep and envelope, and that is what plays until its
 * recorded takes (`Cue.sample`, `samples.ts`) have loaded — or for good, when a file is missing.
 * A cue marked `sampleOnly` has no synthesised form worth hearing and stays silent instead: those
 * are the events that were silent before recordings existed.
 *
 * Pure data plus one pure mapping function, so the whole of "which event makes
 * which noise" is testable without an `AudioContext`.
 */

import {
  EVENT_AREA_STRIKE,
  EVENT_DODGED,
  EVENT_HIT_AVOIDED,
  EVENT_ITEM_BOUGHT,
  EVENT_ITEMS_SOLD,
  EVENT_SKILL_CAST_STARTED,
  EVENT_DODGE_REFUSED,
  EVENT_ENTITY_DAMAGED,
  EVENT_ENTITY_DIED,
  EVENT_LEVEL_UP,
  EVENT_LOOT_DROPPED,
  EVENT_LOOT_TAKEN,
  EVENT_PLAYER_DIED,
  EVENT_SKILL_CAST,
  EVENT_POTION_REFUSED,
  EVENT_POTION_USED,
  EVENT_SKILL_REFUSED,
  type GameEvent,
  type ItemRarity,
} from '../net/protocol';
import type { VoiceCategory } from './voices';

/** The mixer's sub-buses, from §1 of the spec. */
export type Bus = 'combat' | 'environment' | 'ui';

/** One synthesised sound. */
export interface Cue {
  id: string;
  category: VoiceCategory;
  bus: Bus;
  wave: OscillatorType;
  /** Start and end frequency in Hz. A sweep when they differ. */
  from: number;
  to: number;
  /** Seconds, including the release tail. */
  duration: number;
  /** Peak gain before distance attenuation, 0..1. */
  gain: number;
  /**
   * Pitch jitter, ± this fraction, per §3. Without it the ten-thousandth sword
   * swing sounds exactly like the first, which is what makes a sound tiring
   * rather than satisfying.
   */
  jitter: number;
  /**
   * The recorded takes this cue plays when they have loaded — `assets/sfx/<sample>_NN.m4a`
   * (`samples.ts`). Absent: the cue is synthesised only. Present and not loaded: the synthesised
   * sound plays instead, so a missing file never makes the game quieter than it was.
   */
  sample?: string;
  /**
   * **Recorded or nothing.** For the events that were silent before recordings existed: a new
   * beep for every roll, sale and zone change would be a change nobody asked for, so with no
   * take loaded these stay as silent as they were. The synthesised fields are then unused.
   */
  sampleOnly?: boolean;
}

const cue = (c: Cue): Cue => c;

/**
 * Every cue the game can play.
 *
 * The rarity chimes follow the spec's descriptions as closely as a two-parameter
 * synth can: a light clink for NORMAL, a resonant rise for MAGIC, a heavier
 * low-to-high for RARE, and a long high bell for UNIQUE that is meant to cut
 * through a fight — which is why it is the loudest sound in the game.
 */
export const CUES = {
  impact: cue({
    id: 'impact',
    sample: 'impact',
    category: 'IMPACT',
    bus: 'combat',
    wave: 'square',
    from: 220,
    to: 110,
    duration: 0.09,
    gain: 0.3,
    jitter: 0.08,
  }),
  mobAttack: cue({
    id: 'mobAttack',
    sample: 'mob_attack',
    category: 'MOB_ATTACK',
    bus: 'combat',
    wave: 'sawtooth',
    from: 170,
    to: 90,
    duration: 0.14,
    gain: 0.26,
    jitter: 0.06,
  }),
  death: cue({
    id: 'death',
    sample: 'death',
    category: 'IMPACT',
    bus: 'combat',
    wave: 'triangle',
    from: 200,
    to: 60,
    duration: 0.32,
    gain: 0.32,
    jitter: 0.05,
  }),
  skillCast: cue({
    id: 'skillCast',
    sample: 'skill_cast',
    category: 'SKILL',
    bus: 'combat',
    wave: 'sine',
    from: 420,
    to: 880,
    duration: 0.2,
    gain: 0.3,
    jitter: 0.04,
  }),
  dropNormal: cue({
    id: 'dropNormal',
    sample: 'drop_normal',
    category: 'LOOT',
    bus: 'ui',
    wave: 'square',
    from: 900,
    to: 860,
    duration: 0.08,
    gain: 0.16,
    jitter: 0.03,
  }),
  dropMagic: cue({
    id: 'dropMagic',
    sample: 'drop_magic',
    category: 'LOOT',
    bus: 'ui',
    wave: 'sine',
    from: 1046,
    to: 1568,
    duration: 0.28,
    gain: 0.22,
    jitter: 0.02,
  }),
  dropRare: cue({
    id: 'dropRare',
    sample: 'drop_rare',
    category: 'LOOT',
    bus: 'ui',
    wave: 'triangle',
    from: 660,
    to: 1320,
    duration: 0.38,
    gain: 0.28,
    jitter: 0.02,
  }),
  dropUnique: cue({
    id: 'dropUnique',
    sample: 'drop_unique',
    category: 'LOOT',
    bus: 'ui',
    wave: 'sine',
    from: 1568,
    to: 2093,
    duration: 0.7,
    gain: 0.36,
    jitter: 0,
  }),
  pickup: cue({
    id: 'pickup',
    sample: 'pickup',
    category: 'LOOT',
    bus: 'ui',
    wave: 'square',
    from: 1200,
    to: 1400,
    duration: 0.06,
    gain: 0.14,
    jitter: 0.04,
  }),
  levelUp: cue({
    id: 'levelUp',
    sample: 'level_up',
    category: 'UI',
    bus: 'ui',
    wave: 'sine',
    from: 523,
    to: 1046,
    duration: 0.55,
    gain: 0.34,
    jitter: 0,
  }),
  /**
   * A refused cast. The spec's "low-pitch buzz" from §4 — a falling square wave,
   * quiet and short, so it reads as *that did not work* rather than as an alarm.
   */
  /**
   * Drinking: a short wet swell rather than a chime.
   *
   * On the UI bus and not the combat one, because it is feedback on something the
   * player did rather than something that happened in the world — and a draught
   * taken mid-fight must not be voice-stolen by the swings around it.
   */
  potion: cue({
    id: 'potion',
    sample: 'potion',
    category: 'UI',
    bus: 'ui',
    wave: 'sine',
    from: 300,
    to: 620,
    duration: 0.22,
    gain: 0.24,
    jitter: 0.03,
  }),
  refused: cue({
    id: 'refused',
    sample: 'refused',
    category: 'UI',
    bus: 'ui',
    wave: 'square',
    from: 180,
    to: 120,
    duration: 0.14,
    gain: 0.18,
    jitter: 0,
  }),
  /**
   * A roll that did not happen: a short, dry clank an octave below the refusal buzz.
   *
   * Its own cue rather than a second use of `refused`, because the two fire on
   * different keys and the player has to be able to tell them apart **without
   * looking** — a mashed spacebar during a cooldown is the loudest case. A triangle
   * where `refused` is a square, and a register low enough to read as a body failing
   * rather than as an interface saying no.
   */
  dodgeBlocked: cue({
    id: 'dodgeBlocked',
    sample: 'dodge_blocked',
    category: 'UI',
    bus: 'ui',
    wave: 'triangle',
    from: 96,
    to: 62,
    duration: 0.11,
    gain: 0.2,
    jitter: 0.02,
  }),
  playerDeath: cue({
    id: 'playerDeath',
    sample: 'player_death',
    category: 'UI',
    bus: 'ui',
    wave: 'sine',
    from: 240,
    to: 55,
    duration: 1.2,
    gain: 0.38,
    jitter: 0,
  }),
  // ── Recorded only: silent before the recordings, silent without them ──────────────
  //
  // The synthesised fields below are never played (`sampleOnly`), and are filled with the sound
  // the cue would make if it ever became synthesisable, so the type stays one shape.
  dodge: cue({ id: 'dodge', sample: 'dodge', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 400, to: 200, duration: 0.3, gain: 0.26, jitter: 0.05 }),
  evade: cue({ id: 'evade', sample: 'evade', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 900, to: 500, duration: 0.2, gain: 0.24, jitter: 0.06 }),
  areaStrike: cue({ id: 'areaStrike', sample: 'area_strike', sampleOnly: true, category: 'IMPACT', bus: 'combat', wave: 'triangle', from: 120, to: 50, duration: 0.5, gain: 0.32, jitter: 0.05 }),
  castStart: cue({ id: 'castStart', sample: 'cast_start', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 300, to: 600, duration: 0.5, gain: 0.2, jitter: 0.03 }),
  coinsSell: cue({ id: 'coinsSell', sample: 'coins_sell', sampleOnly: true, category: 'UI', bus: 'ui', wave: 'square', from: 1400, to: 1600, duration: 0.4, gain: 0.2, jitter: 0.03 }),
  coinsBuy: cue({ id: 'coinsBuy', sample: 'coins_buy', sampleOnly: true, category: 'UI', bus: 'ui', wave: 'square', from: 1300, to: 1500, duration: 0.4, gain: 0.2, jitter: 0.03 }),
  // Read off the snapshot rather than an event — see `derived.ts`.
  zoneChange: cue({ id: 'zoneChange', sample: 'zone_change', sampleOnly: true, category: 'UI', bus: 'ui', wave: 'sine', from: 200, to: 800, duration: 1, gain: 0.24, jitter: 0 }),
  swingSword: cue({ id: 'swingSword', sample: 'swing_sword', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 500, to: 250, duration: 0.3, gain: 0.18, jitter: 0.06 }),
  swingAxe: cue({ id: 'swingAxe', sample: 'swing_axe', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 450, to: 220, duration: 0.3, gain: 0.18, jitter: 0.06 }),
  swingMace: cue({ id: 'swingMace', sample: 'swing_mace', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 400, to: 200, duration: 0.3, gain: 0.18, jitter: 0.06 }),
  swingDagger: cue({ id: 'swingDagger', sample: 'swing_dagger', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 700, to: 350, duration: 0.25, gain: 0.16, jitter: 0.06 }),
  swingStaff: cue({ id: 'swingStaff', sample: 'swing_staff', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 380, to: 190, duration: 0.3, gain: 0.18, jitter: 0.06 }),
  swingBow: cue({ id: 'swingBow', sample: 'swing_bow', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 900, to: 600, duration: 0.25, gain: 0.18, jitter: 0.05 }),
  // A skill's cast, by the family its authored `sfx_cast` key names (`castFamily`). Falls back to
  // `skillCast`, so a family with no recording is the sound every cast had before.
  fireCast: cue({ id: 'fireCast', sample: 'fire_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  frostCast: cue({ id: 'frostCast', sample: 'frost_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  lightningCast: cue({ id: 'lightningCast', sample: 'lightning_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  shadowCast: cue({ id: 'shadowCast', sample: 'shadow_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  holyCast: cue({ id: 'holyCast', sample: 'holy_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  arrowCast: cue({ id: 'arrowCast', sample: 'arrow_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  buffCast: cue({ id: 'buffCast', sample: 'buff_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  physicalCast: cue({ id: 'physicalCast', sample: 'physical_cast', sampleOnly: true, category: 'SKILL', bus: 'combat', wave: 'sine', from: 420, to: 880, duration: 0.3, gain: 0.3, jitter: 0.04 }),
  // A hit's element (`damageType` on EVENT_ENTITY_DAMAGED). Without a recording a fire hit falls
  // back to `impact` / `mobAttack` — see `hitCue` — so an element never silences a hit.
  fireImpact: cue({ id: 'fireImpact', sample: 'fire_impact', sampleOnly: true, category: 'IMPACT', bus: 'combat', wave: 'square', from: 220, to: 110, duration: 0.3, gain: 0.3, jitter: 0.06 }),
  frostImpact: cue({ id: 'frostImpact', sample: 'frost_impact', sampleOnly: true, category: 'IMPACT', bus: 'combat', wave: 'square', from: 220, to: 110, duration: 0.3, gain: 0.3, jitter: 0.06 }),
  lightningImpact: cue({ id: 'lightningImpact', sample: 'lightning_impact', sampleOnly: true, category: 'IMPACT', bus: 'combat', wave: 'square', from: 220, to: 110, duration: 0.3, gain: 0.3, jitter: 0.06 }),
  voidImpact: cue({ id: 'voidImpact', sample: 'shadow_impact', sampleOnly: true, category: 'IMPACT', bus: 'combat', wave: 'square', from: 220, to: 110, duration: 0.3, gain: 0.3, jitter: 0.06 }),
} as const satisfies Record<string, Cue>;

/** The drop chime for each rarity, per §4. */
export const DROP_CUES: Record<ItemRarity, Cue> = {
  NORMAL: CUES.dropNormal,
  MAGIC: CUES.dropMagic,
  RARE: CUES.dropRare,
  UNIQUE: CUES.dropUnique,
};

/**
 * What a loot filter's `PlayAlertSound n` actually plays.
 *
 * The filter language takes an opaque small integer rather than a filename, because a sound
 * here is a cue, not an asset — recorded or synthesised is the cue's business. This is the
 * table that gives those integers meaning, and it is the only place they have any.
 *
 * The parser accepts 0-15, which is deliberately wider than this map: sixteen slots is
 * room for a palette to grow into, and refusing an id at parse time because no cue is
 * bound to it yet would make adding a cue a breaking change to every filter that had
 * worked around its absence. An id with nothing bound **stays silent** — the same rule a
 * drop of unknown rarity follows. Understating a unique by substituting the plain clink
 * is worse than saying nothing, and a player who wrote `PlayAlertSound 9` hears the
 * silence and is told about it by the editor, which lists every unbound id in the file.
 *
 * The five that are bound run loudest-first, so the number reads like the tier it
 * usually accompanies: 1 for a unique, 4 for trash, 0 for deliberate silence.
 */
export const ALERT_CUES: Readonly<Record<number, Cue | null>> = Object.freeze({
  /** Silence, on purpose: a rule that wants a beam and no noise. */
  0: null,
  1: CUES.dropUnique,
  2: CUES.dropRare,
  3: CUES.dropMagic,
  4: CUES.dropNormal,
});

/** Highest id `ALERT_CUES` could ever bind, matching the parser's `MAX_SOUND_ID`. */
export const MAX_ALERT_SOUND_ID = 15;

/**
 * The cue for an alert id, or null when there is nothing bound to it.
 *
 * Null covers three different situations on purpose — `0` meaning silence, an id in
 * range with no cue, and an id out of range entirely — because the caller's response to
 * all three is the same: play nothing. Telling them apart is the *editor's* job, and it
 * uses `isBoundAlertSound` for it.
 */
export function alertCue(id: number): Cue | null {
  return ALERT_CUES[id] ?? null;
}

/**
 * Whether an id means something, silence included.
 *
 * Separate from `alertCue` because an editor has to distinguish "you asked for silence"
 * from "you asked for a sound that does not exist" — and a bare null cannot.
 */
export function isBoundAlertSound(id: number): boolean {
  return Object.prototype.hasOwnProperty.call(ALERT_CUES, id);
}

/** Where a sound comes from, and whether it is the player's own. */
export interface CueSource {
  /** World position, or null for a sound with no place — a UI sting. */
  at: { x: number; z: number } | null;
}

/**
 * What the loot filter says about a drop, as far as its sound is concerned.
 *
 * A structural slice rather than `FilterVerdict` itself, so this module keeps knowing
 * nothing about `game/filter` — and so a test can state the three cases in a line each.
 */
export interface LootSoundVerdict {
  /** `PlayAlertSound n`, or undefined when no rule set one. */
  soundId?: number;
  /** True when a **rule** hid the item. The built-in Rare+ fallback is not a rule. */
  hidden: boolean;
}

/** What the world has to tell `cueFor` to resolve an event. */
export interface CueContext {
  /** The local player's id, so their own sounds are told apart from a stranger's. */
  selfId: string | null;
  /** Where an entity is, or null if it is not in the snapshot. */
  positionOf(id: string): { x: number; z: number } | null;
  /** The rarity of a ground item, or null if it is not on the floor. */
  rarityOf(lootId: string): ItemRarity | null;
  /**
   * What the player's loot filter says about a drop, or null when it said nothing.
   *
   * Optional so the mapping is still exercisable with no filter in the picture at all —
   * which is also the production behaviour before a filter is loaded.
   */
  filterOf?(lootId: string): LootSoundVerdict | null;
  /** A skill's authored `sfx_cast` key, from the catalogue. Optional, like `filterOf`. */
  skillSoundOf?(skillId: string): string | undefined;
}

/** A cue and where to play it, or null when an event makes no sound. */
export interface CuePlan extends CueSource {
  cue: Cue;
  /**
   * What plays instead when `cue` is recorded-only and has no take loaded. A fire hit's fallback is
   * the plain impact: an element must never silence a hit. Absent: silence, as the event had.
   */
  fallback?: Cue;
}

/**
 * Which cast family an authored `sfx_cast` key belongs to, by the words in it — **first match
 * wins**, and anything unmatched (swings, slashes, stomps, roars, dashes) is physical.
 *
 * Read off content rather than a per-skill table because the content already says it: 88 keys
 * like `sfx_fireball_launch` and `sfx_frost_nova_cast` name their sound, and a table would be a
 * hand-mirrored copy of them. Chosen, not specified: the words and their order. `castFamily.test`
 * resolves every key in `skills.yaml` and requires every family to be reached.
 */
const CAST_WORDS: readonly [RegExp, keyof typeof CUES][] = [
  [/fire|flame|meteor|magma|burn|sear/, 'fireCast'],
  [/frost|ice|blizzard|glacier|cold/, 'frostCast'],
  [/lightning|thunder|electric|zap|shock|storm/, 'lightningCast'],
  [/holy|divine|light_pillar|sunray|sanctuary|cleanse|god_|choir/, 'holyCast'],
  [/void|shadow|drain|gravity|singularity|soul|stealth|phantom|time_warp|teleport|blink|illusion/, 'shadowCast'],
  [/bow|crossbow|twang|sniper|arrow/, 'arrowCast'],
  [/shield|buff|fortress|surge|powerup|absorb|anchor|mana|sanctum|convert/, 'buffCast'],
];

export function castFamily(sfxCast: string | undefined): Cue {
  if (!sfxCast) return CUES.physicalCast;
  for (const [words, cue] of CAST_WORDS) if (words.test(sfxCast)) return CUES[cue];
  return CUES.physicalCast;
}

/** The swing cue for a sample name from `SWING_SAMPLE` (`derived.ts`). */
export const SWING_CUES: Readonly<Record<string, Cue>> = {
  swing_sword: CUES.swingSword,
  swing_axe: CUES.swingAxe,
  swing_mace: CUES.swingMace,
  swing_dagger: CUES.swingDagger,
  swing_staff: CUES.swingStaff,
  swing_bow: CUES.swingBow,
};

/** The recorded impact for a hit's element, by the wire's `damageType`. Absent means PHYSICAL. */
const ELEMENT_IMPACT: Readonly<Record<string, Cue>> = {
  FIRE: CUES.fireImpact,
  COLD: CUES.frostImpact,
  LIGHTNING: CUES.lightningImpact,
  VOID: CUES.voidImpact,
};

/**
 * Which sound an event makes.
 *
 * Returns null for events that are deliberately silent. `EVENT_XP_GAINED` is the
 * loud example: it fires on every kill alongside a death and often a drop, and a
 * fourth simultaneous sound for a number the HUD already shows is noise.
 *
 * Pure — every fact it needs comes through `CueContext` — so the whole mapping is
 * testable with a fake world.
 */
export function cueFor(event: GameEvent, ctx: CueContext): CuePlan | null {
  switch (event.type) {
    case EVENT_ENTITY_DAMAGED: {
      // Who swung decides what it sounds like: the player's hits are a bright
      // impact, a monster's are the duller swing that means *you* are being hit.
      const fromPlayer = event.sourceId === ctx.selfId;
      const physical = fromPlayer ? CUES.impact : CUES.mobAttack;
      // An elemental hit sounds like its element — a fireball does not land like a sword — and
      // falls back to the physical sound when that element has no recording.
      const element = event.damageType ? ELEMENT_IMPACT[event.damageType] : undefined;
      return element
        ? { cue: element, fallback: physical, at: ctx.positionOf(event.entityId) }
        : { cue: physical, at: ctx.positionOf(event.entityId) };
    }

    case EVENT_ENTITY_DIED:
      return { cue: CUES.death, at: ctx.positionOf(event.entityId) };

    case EVENT_PLAYER_DIED:
      // Only your own death. Someone else dying across the map should not sound
      // like the end of your run.
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.playerDeath, at: null };

    case EVENT_LEVEL_UP:
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.levelUp, at: null };

    case EVENT_LOOT_DROPPED: {
      const rarity = ctx.rarityOf(event.entityId);
      // A drop whose item is not on the floor yet has no rarity to chime. Falling
      // back to the NORMAL clink would understate a unique, which is the one
      // sound in the game that must never be wrong.
      if (rarity === null) return null;

      // The loot filter, if one has an opinion. Three outcomes, in order:
      //
      //  - A **rule** hid the item: silent, per the spec's Filtered Out row. Note that
      //    the built-in Rare+ fallback does *not* set `hidden` — silencing every Normal
      //    drop for a player who never wrote a filter would be taking something away
      //    that they never asked to lose.
      //  - The rule named a sound: play that, or **nothing at all** if the id has no cue
      //    bound to it. Substituting the rarity chime for an unresolvable id would make
      //    a broken filter indistinguishable from a working one.
      //  - The rule said nothing about sound: the rarity chime, exactly as before.
      const verdict = ctx.filterOf?.(event.entityId) ?? null;
      if (verdict?.hidden) return null;
      if (verdict?.soundId !== undefined) {
        const alert = alertCue(verdict.soundId);
        return alert === null ? null : { cue: alert, at: ctx.positionOf(event.entityId) };
      }

      return { cue: DROP_CUES[rarity], at: ctx.positionOf(event.entityId) };
    }

    case EVENT_LOOT_TAKEN:
      return { cue: CUES.pickup, at: null };

    case EVENT_SKILL_CAST:
      if (event.entityId !== ctx.selfId) return null;
      // The skill's own family (`castFamily`), falling back to the generic cast when that family
      // has no recording — or when there is no catalogue to ask.
      return { cue: castFamily(event.sourceId ? ctx.skillSoundOf?.(event.sourceId) : undefined), fallback: CUES.skillCast, at: null };

    case EVENT_POTION_USED:
      // Only your own. Another player drinking across the map is not a sound you
      // need, and the belt is private anyway.
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.potion, at: null };

    case EVENT_POTION_REFUSED:
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.refused, at: null };

    case EVENT_SKILL_REFUSED:
      // The server only sends this to whoever tried, but check anyway — a sound
      // for someone else's mistake would be baffling.
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.refused, at: null };

    case EVENT_DODGE_REFUSED:
      // Same rule as every other refusal: only your own. The roll gets its own
      // sound because it is on its own key — a player who cannot hear the
      // difference is back to a key that does nothing.
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.dodgeBlocked, at: null };

    // ── Silent until recordings existed; `sampleOnly`, so silent again without them ──────

    case EVENT_DODGED:
      // Sent to everyone so a spectator can animate the roll; the sound is only yours, as the
      // refusal's is — someone else's roll across the map is not information.
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.dodge, at: null };

    case EVENT_HIT_AVOIDED:
      // The one event that reports something *not* happening: the i-frames worked. Yours only.
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.evade, at: null };

    case EVENT_AREA_STRIKE:
      // A slam or a blast, from **where it landed** (`aimX/aimZ`), not from whoever struck — and
      // anyone's, because a monster's slam is exactly the sound that says to move.
      return {
        cue: CUES.areaStrike,
        at: event.aimX !== undefined && event.aimZ !== undefined ? { x: event.aimX, z: event.aimZ } : ctx.positionOf(event.entityId),
      };

    case EVENT_SKILL_CAST_STARTED:
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.castStart, at: null };

    // EVENT_ZONE_CHANGED stays silent, and not for lack of a recording: it is emitted into the
    // *destination* instance, so everyone already there receives it whenever anyone arrives, and
    // its entityId is the layout kind rather than a player. The sound belongs on this client's own
    // ZONE frame instead — not wired yet.

    case EVENT_ITEMS_SOLD:
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.coinsSell, at: null };

    case EVENT_ITEM_BOUGHT:
      if (event.entityId !== ctx.selfId) return null;
      return { cue: CUES.coinsBuy, at: null };

    default:
      return null;
  }
}
